import * as React from 'react';
import FieldAlert from '../field-alert/field-alert';

function buildEventsObject(eventsHash, propEntry) {
  const [propName, propValue] = propEntry;

  if (propName.startsWith('on') && typeof propValue === 'function') {
    eventsHash[propName] = propValue;
  }

  return eventsHash;
}

function findEventCallbacks(propsObj) {
  return Object.entries(propsObj).reduce(buildEventsObject, {});
}

interface IValidateField {
  children: React.ReactElement<any>;
  onBlur: (e: any) => string;
  onChange: (e: any) => string;
  className?: string;
}

interface IValidateFieldState {
  error?: string;
}

export default class ValidateField extends React.Component<IValidateField, IValidateFieldState> {
  constructor(props: IValidateField) {
    super(props);

    this.state = {
      error: '',
    };
  }

  componentWillMount() {
    this.generateEventHandlers();
  }

  generateEventHandlers = () => {
    /**
     * If there's no field to validate, we have no work to do.
     */
    if (!this.props.children) {
      return;
    }

    /**
     * Extract all the `on*` events that the user has passed into the field
     * element, and store them in an object.
     *
     * @type {Object.<string, function>} - the key is callback name e.g.: `onClick`
     */
    const fieldEvents = findEventCallbacks(this.props.children.props);

    /**
     * Do the same for all the triggers that invoke a validation function.
     */
    const validationEvents = findEventCallbacks(this.props);

    /**
     * Here we patch each of the validation events so that when they are
     * called, they actually invoke their passed-in validation functions,
     * with the right arguments. These functions are expected to return
     * an error message, if applicable.
     *
     * Also, once the validation function has been invoked, if the field
     * has an event listener of the same type (e.g.: an `onBlur` for both
     * the `<ValidateField>` and it's containing field element, we make sure
     * to invoke the event callback on the child field element as well.
     *
     * @param {Object.<string, function>} callbacksHash
     * @param {string} callbackName
     * @param {function} eventFn
     * @return {Object.<string, function>}
     */
    const patchEventCallbacks = (callbacksHash, [callbackName, eventFn]) => {
      callbacksHash[callbackName] = (event) => {
        /**
         * Ensure that the `event` sticks around long enough for the
         * `fieldEvents` callback to be able to use it.
         */
        event.persist();

        const error = eventFn(event, this.state.error, this.props.children.props);

        /**
         * Here we invoke the user's validation function for this event,
         * and will set the error message based on whatever comes back
         * from it. If the field is valid, it should return something
         * falsy.
         */
        this.setState({
          error,
        });
        /**
         * If the field had its own event listener / callback of the
         * same type as the `<ValidateField>` did (for the current value
         * of `callbackName` in this iterator, we must invoke that
         * event handler callback as well, since we are not preventing
         * the bubbling of the event.
         * As a second argument, we send whether Component detects a valid state,
         * that is, no error is defined.
         */
        const isValid = !error || error === '' || typeof error === 'undefined';
        if (fieldEvents[callbackName]) {
          fieldEvents[callbackName](event, isValid);
        }
      };

      return callbacksHash;
    };

    this.eventHandlers = {
      ...fieldEvents,
      ...Object.entries(validationEvents).reduce(patchEventCallbacks, {}),
    };
  };

  render() {
    const { children, className = '' } = this.props;
    const { error } = this.state;
    /**
     * If there's no field to validate, we have no work to do.
     */
    if (!children) {
      return null;
    }

    return (
      <FieldAlert className={className} error={error}>
        {React.cloneElement(children, this.eventHandlers)}
      </FieldAlert>
    );
  }
}
